<template>
  <div id="app">
    <a
      href="#main-content"
      class="skip-link"
      @click.prevent="focusContent"
    >
      Pular para o conteúdo
    </a>

    <div
      :class="{
        'is-mobile': isMobile,
        'no-header': !renderHeader,
        'no-nav': !renderNav,
        'has-consult': isConsultOnly,
      }"
      :style="{
        '--nav-max-width': navigationWidth,
      }"
      class="layout"
    >
      <!-- top bar -->
      <template v-if="renderHeader">
        <the-logo
          v-if="!isMobile"
          :class="{
            'mouseover-pin': mouseOverPinState,
            'has-toggle': !isMobile && renderNav,
          }"
          class="layout__logo"
          @click.native="retract"
        />
        <button
          v-if="!isMobile && renderNav"
          ref="toggle"
          type="button"
          class="layout__toggle"
          aria-label="Menu principal"
          :aria-expanded="String(!pinned)"
          aria-controls="navigation-menu"
          :title="pinned ? 'Expandir menu' : 'Recolher menu'"
          @click="retract"
          @mouseenter="mouseOverPin(true)"
          @mouseleave="mouseOverPin(false)"
        >
          <i
            class="fas"
            :class="pinned ? 'fa-angle-double-right' : 'fa-angle-double-left'"
            aria-hidden="true"
          />
        </button>
        <the-user
          ref="user"
          class="layout__user"
          :menu-open="retractNavigation"
          @toggle="handlerToggleRetractNavigation"
          @accounts="showPromoterAccess = true"
        />

        <promoter-consult-banner
          v-if="isConsultOnly"
          class="layout__consult"
          @switch="showPromoterAccess = true"
        />
      </template>

      <!-- side bar -->
      <template v-if="renderNav">
        <the-navigation
          v-if="!isMobile || (isMobile && retractNavigation)"
          id="navigation-menu"
          ref="navigation"
          :class="{
            expand: pinned && !minimized && !isMobile,
            'mouseover-pin': mouseOverPinState,
          }"
          class="layout__nav"
          @mouseover="mouseOverRetract"
          @expand="expandNavigation"
          @close="closeMobileNavigation"
        />
      </template>

      <!-- content -->
      <router-view
        v-if="bootstrapped"
        id="main-content"
        class="layout__content"
        role="main"
        :inert="isMobile && retractNavigation"
      />
    </div>

    <PromoterAccessModal
      v-if="showPromoterAccess"
      @close="showPromoterAccess = false"
    />

    <loader
      v-if="loading"
      :object="loaderConfig.color.light"
      :color1="loaderConfig.color.secondary"
      :color2="loaderConfig.color.primary"
      :opacity="loaderConfig.opacity"
      :bg="loaderConfig.backgroundColor"
      :size="loaderConfig.size"
      :speed="loaderConfig.speed"
      :name="loaderConfig.name"
      :objectbg="loaderConfig.objectbg"
    />

    <TheNoticeVersion v-if="noticeVersion" />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

import useTheme from '@/composables/theme';
import { useAppVersion } from '@/composables/useAppVersion';

import loadingMixin from '@/mixins/loadingView.mixin';
import verifyDataUserMixin from '@/mixins/verifyDataUser.mixin';

import themeFallback from '@/data/theme.json';
import { navigationOwner, recentVisit, recordRecent } from '@/utils/navigationShortcuts';

import TheLogo from '@/components/TheLogo.vue';
import TheUser from '@/components/TheUser.vue';
import TheNavigation from '@/components/TheNavigation.vue';
import PromoterConsultBanner from '@/components/PromoterConsultBanner.vue';

const { domain, loadTheme } = useTheme();

export default {
  name: 'App',
  components: {
    TheLogo,
    TheUser,
    TheNavigation,
    PromoterConsultBanner,

    PromoterAccessModal: () => import('@/components/PromoterAccessModal.vue'),

    TheNoticeVersion: () => import('@/components/TheNoticeVersion.vue'),
  },
  mixins: [
    verifyDataUserMixin,
    loadingMixin,
  ],
  provide() {
    return {
      domain: this.domain,
    };
  },
  data: () => ({
    // control render
    renderHeader: false,
    renderNav: false,
    // router-view só monta depois que handleTheme() termina, pra evitar que
    // a página monte e dispare suas próprias chamadas em paralelo com o
    // carregamento do tema (ver created()).
    bootstrapped: false,

    // control visibility
    retractNavigation: false,

    // navigation
    mouseOverPinState: false,
    mouseOverPinTimeout: null,
    mouseOverTimeout: 350,

    // promoter access
    showPromoterAccess: false,

    // version
    noticeVersion: false,
    versionRouterUnsub: null,
    pendingVersionReload: false,
  }),
  computed: {
    ...mapState({
      $_retract: (state) => state.retract,
      theme: (state) => state.theme.theme,
      user: (state) => state.user.userInfo,
      token: (state) => state.app.token,
    }),
    ...mapGetters({
      isMobile: 'app/isMobile',
      isPromoterActive: 'user/isPromoterActive',
    }),
    // Only after /users/me: a strip on every reload says the opposite of what it means.
    shortcutsOwner() {
      return navigationOwner({ userId: this.user?.id, token: this.token });
    },
    isConsultOnly() {
      return Boolean(this.user?.id) && !this.isPromoterActive && this.renderHeader;
    },

    // global
    domain() {
      return domain();
    },
    hasTheme() {
      return !!this.theme.business_info;
    },
    navigationWidth() {
      if (this.isMobile) return '230px';

      return this.pinned ? '90px' : 'var(--nav-expanded-width)';
    },
    isFullLayoutView() {
      if (!this.$route.name) return false;

      return ![
        'login',
        'recover-password',
        'change-password',
        'inss-video-call',
      ].includes(this.$route.name);
    },

    // loader
    loaderConfig() {
      return {
        color: this.theme?.styles?.color || themeFallback.styles.color,
        opacity: this.hasTheme ? 80 : 100,
        backgroundColor: this.hasTheme ? '#343a40' : '#fff',
        size: 5,
        speed: 2,
        name: 'circular',
        objectbg: '#999793',
      };
    },

    // navigation
    minimized: {
      set(payload) {
        this.setMinimized(payload);
      },
      get() {
        return this.$_retract.minimized;
      },
    },
    pinned: {
      set(payload) {
        this.setPinned(payload);
      },
      get() {
        return this.$_retract.pinned;
      },
    },
    mouseover: {
      set(payload) {
        this.setMouseover(payload);
      },
      get() {
        return this.$_retract.mouseover;
      },
    },
    automatic: {
      set(payload) {
        this.setAutomatic(payload);
      },
      get() {
        return this.$_retract.automatic;
      },
    },
  },
  watch: {
    user() {
      this.getStatusList();
    },
    retractNavigation(isOpen) {
      if (isOpen && this.isMobile) this.$nextTick(() => this.$refs.navigation?.focusMenu());
    },
    isMobile(mobile) {
      if (!mobile) return;

      const frame = [this.$refs.navigation?.$el, this.$refs.toggle];
      const focusInFrame = frame.some((node) => node?.contains(document.activeElement));

      if (this.mouseover) this.collapseHoverExpansion();
      if (focusInFrame) this.closeMobileNavigation();
    },
    $route() {
      if (this.mouseover && !this.minimized && !this.isMobile) this.collapseHoverExpansion();

      this.renderHeader = this.isFullLayoutView;
      this.renderNav = this.isFullLayoutView;

      if (this.retractNavigation && this.isMobile) this.closeMobileNavigation();
      else this.retractNavigation = false;

      this.rememberVisit();
    },
    shortcutsOwner(owner) {
      if (owner) this.rememberVisit();
    },
  },
  async created() {
    if (this.pinned && this.mouseover) this.collapseHoverExpansion();

    // global
    window.addEventListener('refreshToken', this.handlerRefreshToken);
    window.addEventListener('renderHeader', this.handlerRenderHeader);
    window.addEventListener('renderNav', this.handlerRenderNav);
    window.addEventListener('deployVersionObserved', this.handlerDeployVersionObserved);

    // setting system up
    await this.handleTheme();
    this.bootstrapped = true;
    this.renderHeader = this.isFullLayoutView;
    this.renderNav = this.isFullLayoutView;

    // app version :: initial check + recurrent triggers
    await this.handleVersionCheck({ force: true });
    this.versionRouterUnsub = this.$router.afterEach(() => {
      this.handleVersionCheck();
    });
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },
  async mounted() {
    this.toggleBodyClass(true);
  },
  beforeDestroy() {
    window.removeEventListener('refreshToken', this.handlerRefreshToken);
    window.removeEventListener('renderHeader', this.handlerRenderHeader);
    window.removeEventListener('renderNav', this.handlerRenderNav);
    window.removeEventListener('deployVersionObserved', this.handlerDeployVersionObserved);

    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    if (this.versionRouterUnsub) this.versionRouterUnsub();

    this.toggleBodyClass(false);
  },
  methods: {
    rememberVisit() {
      this.$nextTick(() => {
        if (!this.shortcutsOwner) return;

        const visit = recentVisit({
          route: this.$route,
          title: this.$meta().refresh().metaInfo.titleChunk,
          framed: this.isFullLayoutView,
          now: Date.now(),
        });

        if (visit) recordRecent(this.shortcutsOwner, visit);
      });
    },
    ...mapActions({
      getStatusList: 'app/SET_STATUSES',
      setToken: 'app/SET_TOKEN',
      setMinimized: 'retract/SET_MINIMIZED',
      setPinned: 'retract/SET_PINNED',
      setMouseover: 'retract/SET_MOUSEOVER',
      setAutomatic: 'retract/SET_AUTOMATIC',
    }),

    // body
    toggleBodyClass(isTrue) {
      const [className] = this.domain.split('.');

      if (isTrue) window.document.body.classList.add(className);
      else window.document.body.classList.remove(className);
    },
    setFavicon() {
      const link = document.createElement('link');

      link.setAttribute('rel', 'shortcut icon');
      link.setAttribute('type', 'image/png');
      link.setAttribute('href', this.theme?.styles?.logo?.favicon);

      document.head.appendChild(link);
    },

    // system
    handlerRefreshToken({ detail }) {
      this.setToken(detail);
    },
    handlerRenderHeader({ detail }) {
      this.renderHeader = detail;
    },
    handlerRenderNav({ detail }) {
      this.renderNav = detail;
    },

    // navigation
    handlerToggleRetractNavigation() {
      this.retractNavigation = !this.retractNavigation;
    },
    focusContent() {
      const content = document.getElementById('main-content');
      if (!content) return;

      content.setAttribute('tabindex', '-1');
      content.addEventListener('blur', () => content.removeAttribute('tabindex'), { once: true });
      content.focus();
    },
    closeMobileNavigation() {
      this.retractNavigation = false;
      this.$nextTick(() => this.$refs.user?.focusMenuButton());
    },
    retract() {
      if (this.isMobile) return;

      const value = !this.pinned;

      clearTimeout(this.automatic);
      this.mouseover = false;
      this.minimized = value;
      this.pinned = value;
    },
    mouseOverRetract(event) {
      clearTimeout(this.automatic);
      this.automatic = setTimeout(() => {
        this.minimized = event;
        this.mouseover = !this.minimized;
      }, this.mouseOverTimeout);
    },
    collapseHoverExpansion() {
      clearTimeout(this.automatic);
      this.mouseover = false;
      this.minimized = true;
    },
    expandNavigation() {
      clearTimeout(this.automatic);
      this.minimized = false;
      this.mouseover = true;
    },
    mouseOverPin(event) {
      clearTimeout(this.mouseOverPinTimeout);
      this.mouseOverPinTimeout = setTimeout(() => {
        this.mouseOverPinState = event;
      }, this.mouseOverTimeout);
    },

    // api
    async handleTheme() {
      this.loading = true;
      try {
        await loadTheme();
      } catch {
        // silent error
      } finally {
        this.loading = false;
        this.setFavicon();
      }
    },

    // version
    async handleVersionCheck({ force = false } = {}) {
      const result = await useAppVersion().validateAppVersion({ force });
      this.actOnVersionResult(result);
    },
    // fed by the X-Deploy-Version-Admin header (see services/api.js) instead of polling
    handlerDeployVersionObserved({ detail }) {
      const result = useAppVersion().reportObservedVersion(detail);
      this.actOnVersionResult(result);
    },
    // capped via useAppVersion().registerAutoReload() — a deploy that never actually
    // converges (e.g. CDN/S3 serving a stale index.html) would otherwise auto-reload forever
    attemptAutoReload() {
      this.pendingVersionReload = false;
      if (useAppVersion().registerAutoReload()) {
        window.location.reload();
        return;
      }
      this.noticeVersion = true;
    },
    actOnVersionResult(result) {
      if (!result?.scheduled) return;

      // only reload a hidden tab — never interrupt one the user is actively on.
      // a visible tab gets the dismissible notice and self-updates next time it's backgrounded
      if (document.visibilityState === 'hidden') {
        this.attemptAutoReload();
        return;
      }

      this.noticeVersion = true;
      this.pendingVersionReload = true;
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'hidden' && this.pendingVersionReload) {
        this.attemptAutoReload();
        return;
      }

      if (document.visibilityState === 'visible') {
        this.handleVersionCheck();
      }
    },
  },
};
</script>

<style lang="scss">
@import './assets/scss/styles.scss';

html,
body {
  margin: 0px !important;
  padding: 0px !important;
  overflow: auto;
}

// analytics
[id*='btn'] * {
  pointer-events: none;
}
</style>

<style lang="scss" scoped>
.skip-link {
  position: absolute;
  top: .5rem;
  left: .5rem;
  z-index: 1000;

  padding: .75rem 1rem;
  border-radius: 4px;
  background-color: $color-secondary;
  color: $color-light;
  font-weight: 600;

  transform: translateY(-200%);

  &:focus-visible {
    outline: 2px solid $color-primary;
    outline-offset: 2px;
    transform: none;
  }
}

#main-content:focus {
  outline: none;
}

.layout {
  --header-height: 60px;
  --consult-height: 0px;
  // 100vh counts the browser bar as hidden, which pushes the header out of sight on mobile.
  --viewport: 100vh;
  --content-height: calc(var(--viewport) - var(--header-height) - var(--consult-height));
  --nav-max-width: 230px;
  --nav-expanded-width: 260px;

  position: relative;

  display: grid;
  width: 100vw;
  height: var(--viewport);
  background-color: $color-primary;

  grid-template-areas: 'logo user' 'nav content';
  grid-template-columns: minmax(auto, var(--nav-max-width)) 1fr;
  grid-template-rows: var(--header-height) 1fr;

  overflow: hidden;

  // The grid is 100vh with overflow hidden: height outside a row disappears.
  &.has-consult {
    --consult-height: 36px;

    grid-template-areas: 'logo user' 'consult consult' 'nav content';
    grid-template-rows: var(--header-height) var(--consult-height) 1fr;

    &.no-nav {
      grid-template-areas: 'logo user' 'consult consult' 'content content';
    }

    // `.is-mobile` comes later and rewrites the rows at the same specificity.
    &.is-mobile {
      --consult-height: 32px;

      grid-template-rows: var(--header-height) var(--consult-height) 1fr;
    }
  }

  &.no-header {
    --content-height: var(--viewport);

    grid-template-rows: 1fr;
    grid-template-areas: 'nav content';
  }

  &.no-nav {
    grid-template-areas: 'logo user' 'content content';
  }

  &.no-header.no-nav {
    grid-template-columns: 1fr;
    grid-template-areas: 'content';
  }

  &.is-mobile {
    --header-height: 44px;

    grid-template-areas: 'user' 'content';
    grid-template-rows: var(--header-height) 1fr;
    grid-template-columns: 100%;

    &.has-consult {
      grid-template-areas: 'user' 'consult' 'content';
    }

    &.no-header {
      grid-template-areas: 'content';
      grid-template-rows: 1fr;
    }

    .layout__nav {
      grid-area: content;
      z-index: 10;

      width: 100%;
    }

    .layout__content {
      position: relative;
    }

    > :deep([class*='content']) {

      $recuo: 1rem;

      padding: 0 $recuo $recuo $recuo;

      > .card {
        margin: $recuo auto;
      }
    }

    :deep(.actions) {
      position: absolute;
      right: 24px;
      top: 24px;
    }
  }

  &:not(.is-mobile) {
    [class*='__logo'] {
      cursor: pointer;
    }
  }

  &__logo,
  &__user {
    height: var(--header-height);
  }

  &__nav,
  &__content {
    height: var(--content-height);
  }

  &__logo {
    grid-area: logo;

    &.has-toggle {
      padding-left: 2.75rem;
    }
  }

  &__toggle {
    @include ghost-control;

    grid-area: logo;
    z-index: 2;

    display: flex;
    width: 2.25rem;
    height: 2.25rem;
    margin-left: 0.5rem;
    padding: 0;
    align-self: center;
    justify-self: start;
    align-items: center;
    justify-content: center;

    i {
      font-size: 1rem;
    }
  }

  &__user {
    grid-area: user;
  }

  &__consult {
    grid-area: consult;
    height: var(--consult-height);
  }

  &__nav {
    grid-area: nav;
    overflow: hidden auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    background-color: $color-surface;

    &.expand {
      position: absolute;
      z-index: 99;

      width: var(--nav-expanded-width);

      box-shadow: 5px 0 3px rgba(#333, 0.1);
    }
  }

  &__content {
    grid-area: content;

    padding: 0 2rem 2rem 2rem;
    background-color: $color-light;
    overflow: auto;
    box-shadow: inset 0.5px 0.5px 1px rgba(10, 10, 10, 0.1);

    box-shadow: inset 1px 1px 1px rgba(10, 10, 10, 0.1);

    > .card {
      margin: 30px auto;

      max-width: var(--max-width);
      box-shadow: 0 1px 1px 1px rgba(10, 10, 10, 0.1);
    }
  }

  .mouseover-pin {
    $color-accent: rgba(var(--color-secondary-rgb), .5);

    &[class*="__nav"],
    &[class*="__logo"] {
      position: relative;

      &::before {
        content: '';

        position: absolute;
        top: 0;
        right: 0;
        z-index: 1;

        display: block;
        width: 2.5px;
        height: 100%;
        background-color: $color-accent;
      }
    }
  }
}

@supports (height: 100dvh) {
  .layout {
    --viewport: 100dvh;
  }
}
</style>
